// src/components/BookDetail.js

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useParams, Link } from "react-router-dom";
import * as BooksAPI from "../BooksAPI"; // Note the path: up one level for BooksAPI
import ShelfChanger from "./ShelfChanger"; // Reusable component for the dropdown

const FALLBACK_THUMBNAIL = "https://via.placeholder.com/200x300?text=No+Cover";

const BookDetail = ({ updateBookShelf }) => {
    // Get the 'id' parameter from the URL
    const { id } = useParams();
    const [book, setBook] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let isMounted = true;
        setLoading(true);

        BooksAPI.get(id)
            .then((data) => {
                if (isMounted) {
                    setBook(data);
                    setLoading(false);
                }
            })
            .catch(() => {
                if (isMounted) {
                    setError(true);
                    setLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [id]);

    if (loading) {
        return <div className="detail-page-message">Loading Book Details...</div>;
    }

    if (error || !book || book.error) {
        return <div className="detail-page-message">Sorry, we couldn't find details for this book.</div>;
    }

    // Process data for display
    const thumbnail = 
        book.imageLinks && book.imageLinks.thumbnail
            ? book.imageLinks.thumbnail.replace('http:', 'https:') // Ensure HTTPS for modern browsers
            : FALLBACK_THUMBNAIL;
            
    const authors = 
        book.authors && Array.isArray(book.authors)
            ? book.authors.join(", ")
            : "Author(s) Unknown";

    const description = book.description || "No description available for this title.";

    return (
        <div className="book-detail-page">
            <Link to="/" className="close-detail-link">
                &larr; Back to Shelves
            </Link>
            
            <div className="detail-card">
                <div className="detail-left">
                    <img src={thumbnail} alt={`Cover of ${book.title}`} className="detail-cover" />
                </div>

                <div className="detail-right">
                    <h1 className="detail-title">{book.title}</h1>
                    <h2 className="detail-authors">{authors}</h2>
                    
                    <div className="detail-meta">
                        <p><strong>Published Date:</strong> {book.publishedDate || 'N/A'}</p>
                        <p><strong>Publisher:</strong> {book.publisher || 'N/A'}</p>
                        <p><strong>Pages:</strong> {book.pageCount || 'N/A'}</p>
                        <p><strong>Categories:</strong> {(book.categories && book.categories.join(', ')) || 'N/A'}</p>
                    </div>

                    <div className="detail-shelf-control">
                        <label htmlFor="shelf-select">Change Shelf:</label>
                        {/* Reuse the shelf changer logic */}
                        <ShelfChanger book={book} changeBookShelf={updateBookShelf} />
                    </div>

                    <div className="detail-description-section">
                        <h3>Description</h3>
                        <p className="detail-description">{description}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

BookDetail.propTypes = {
    updateBookShelf: PropTypes.func.isRequired,
};

export default BookDetail;